// useMemberData.js - Fixed and Complete Version
import React from "react";
import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDInput from "components/MDInput";
import Swal from "sweetalert2";
import Dialog from "@mui/material/Dialog";
import { Slide } from "@mui/material";
import Grid from "@mui/material/Grid";

// PropTypes definitions
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  gender: PropTypes.string,
  department: PropTypes.string,
  ministry: PropTypes.string,
  status: PropTypes.string,
  createdAt: PropTypes.string,
  originalData: PropTypes.object,
});

// Cell Components
const SerialCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value}
  </MDTypography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const MemberIdCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value || "Generating..."}
  </MDTypography>
);
MemberIdCell.propTypes = { value: PropTypes.any.isRequired };

const DepartmentCell = ({ value }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="button" fontWeight="medium">
      {value || "No Department"}
    </MDTypography>
  </MDBox>
);
DepartmentCell.propTypes = { value: PropTypes.string };

const MinistryCell = ({ value }) => (
  <MDTypography variant="button" fontWeight="medium">
    {value || "No Ministry"}
  </MDTypography>
);
MinistryCell.propTypes = { value: PropTypes.string };

const StatusCell = ({ value }) => (
  <MDBox ml={-1}>
    <MDBadge
      badgeContent={value}
      color={value === "active" ? "success" : "error"}
      variant="gradient"
      size="sm"
    />
  </MDBox>
);
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete, handleShow }) => (
  <MDBox display="flex" justifyContent="center" gap={1}>
    <MDButton variant="gradient" color="info" size="small" onClick={() => handleShow(row.original)}>
      <Icon>visibility</Icon>&nbsp;Show
    </MDButton>
    <MDButton variant="gradient" color="info" size="small" onClick={() => handleEdit(row.original)}>
      <Icon>edit</Icon>&nbsp;Edit
    </MDButton>
    <MDButton
      variant="gradient"
      color="error"
      size="small"
      onClick={() => handleDelete(row.original)}
    >
      <Icon>delete</Icon>&nbsp;Delete
    </MDButton>
  </MDBox>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleShow: PropTypes.func.isRequired,
};

// Transition Component
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});
Transition.displayName = "Transition";

// DetailItem Component for Member Details Modal
const DetailItem = ({ label, value, icon }) => (
  <MDBox
    mb={2}
    p={2}
    sx={{
      backgroundColor: "rgba(0,0,0,0.02)",
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.1)",
    }}
  >
    <MDBox display="flex" alignItems="center" mb={0.5}>
      {icon && <Icon sx={{ mr: 1, color: "info.main" }}>{icon}</Icon>}
      <MDTypography variant="caption" fontWeight="bold" color="info">
        {label}
      </MDTypography>
    </MDBox>
    <MDTypography variant="body2" sx={{ pl: icon ? 4 : 0 }}>
      {value || "Not provided"}
    </MDTypography>
  </MDBox>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  icon: PropTypes.string,
};

// Member Details Modal Component
const MemberDetailsModal = ({ member, open, onClose }) => {
  if (!member) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth="xl"
      TransitionComponent={Transition}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
        },
      }}
    >
      <MDBox p={4}>
        {/* Header Section */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          pb={2}
          sx={{ borderBottom: "1px solid rgba(0,0,0,0.1)" }}
        >
          <MDBox display="flex" alignItems="center">
            <Icon sx={{ mr: 2, fontSize: "2rem", color: "info.main" }}>person</Icon>
            <MDTypography variant="h4" color="info">
              Member Profile
            </MDTypography>
          </MDBox>
          <MDButton
            variant="text"
            color="secondary"
            onClick={onClose}
            sx={{ minWidth: "auto", p: 1 }}
          >
            <Icon>close</Icon>
          </MDButton>
        </MDBox>

        {/* Member Header with Photo */}
        <MDBox
          display="flex"
          alignItems="center"
          mb={4}
          p={3}
          sx={{
            backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px",
            background:
              "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
          }}
        >
          {member.photo ? (
            <MDBox
              component="img"
              src={`http://api.ofi-cia.com/laravel/storage/app/public/${member.photo}`}
              alt="Member"
              sx={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                objectFit: "cover",
                mr: 3,
                border: "4px solid white",
                boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
              }}
            />
          ) : (
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                backgroundColor: "info.main",
                mr: 3,
                color: "white",
              }}
            >
              <Icon sx={{ fontSize: "3rem" }}>person</Icon>
            </MDBox>
          )}
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" mb={1}>
              {member.firstName} {member.middleName} {member.surname}
            </MDTypography>
            <MDBox display="flex" alignItems="center" mb={1}>
              <Icon sx={{ mr: 1, color: "info.main" }}>badge</Icon>
              <MDTypography variant="h6" color="info">
                ID: {member.memberId || "Not assigned"}
              </MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center">
              <Icon
                sx={{ mr: 1, color: member.status === "active" ? "success.main" : "error.main" }}
              >
                {member.status === "active" ? "check_circle" : "cancel"}
              </Icon>
              <MDTypography
                variant="button"
                color={member.status === "active" ? "success" : "error"}
                fontWeight="bold"
              >
                {member.status?.toUpperCase()}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Content Sections */}
        <MDBox sx={{ maxHeight: "60vh", overflow: "auto", pr: 1 }}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(102, 126, 234, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(102, 126, 234, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="info">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>person_outline</Icon>
                  Personal Information
                </MDTypography>
                <DetailItem label="First Name" value={member.firstName} icon="person" />
                <DetailItem label="Middle Name" value={member.middleName} icon="person" />
                <DetailItem label="Surname" value={member.surname} icon="person" />
                <DetailItem label="Gender" value={member.gender} icon="wc" />
                <DetailItem label="Date of Birth" value={member.dateOfBirth} icon="cake" />
                <DetailItem label="Marital Status" value={member.maritalStatus} icon="favorite" />
              </MDBox>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(33, 150, 243, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(33, 150, 243, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="primary">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>contact_phone</Icon>
                  Contact Information
                </MDTypography>
                <DetailItem label="Phone Number" value={member.phoneNumber} icon="phone" />
                <DetailItem label="Other Number" value={member.otherNumber} icon="phone" />
                <DetailItem label="WhatsApp Number" value={member.whatsappNumber} icon="chat" />
                <DetailItem label="Email" value={member.email} icon="email" />
              </MDBox>
            </Grid>

            {/* Address Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(76, 175, 80, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(76, 175, 80, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="success">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>location_on</Icon>
                  Address Information
                </MDTypography>
                <DetailItem
                  label="Residential Address"
                  value={member.residentialAddress}
                  icon="home"
                />
                <DetailItem label="Postal Address" value={member.postalAddress} icon="mail" />
                <DetailItem label="Region" value={member.region} icon="public" />
                <DetailItem label="Home Town" value={member.homeTown} icon="location_city" />
                <DetailItem label="Nationality" value={member.nationality} icon="flag" />
                <DetailItem label="Place of Birth" value={member.placeOfBirth} icon="place" />
              </MDBox>
            </Grid>

            {/* Professional Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(255, 152, 0, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 152, 0, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="warning">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>work</Icon>
                  Professional Information
                </MDTypography>
                <DetailItem label="Occupation" value={member.occupation} icon="business_center" />
                <DetailItem label="Profession" value={member.profession} icon="work_outline" />
                <DetailItem label="Rank/Position" value={member.rank} icon="military_tech" />
                <DetailItem label="Employment" value={member.employment} icon="work" />
                <DetailItem label="Employer" value={member.nameOfEmployer} icon="domain" />
                <DetailItem label="Education" value={member.education} icon="school" />
              </MDBox>
            </Grid>

            {/* Family Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(233, 30, 99, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(233, 30, 99, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="error">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>family_restroom</Icon>
                  Family Information
                </MDTypography>
                <DetailItem label="Spouse Name" value={member.spouseName} icon="favorite" />
                <DetailItem label="Spouse Phone" value={member.spousePhoneNumber} icon="phone" />
                <DetailItem
                  label="Date of Marriage"
                  value={member.dateOfMarriage}
                  icon="favorite_border"
                />
                <DetailItem
                  label="Number of Children"
                  value={member.numberOfChildren}
                  icon="child_care"
                />
                <DetailItem label="Male Children" value={member.numberOfMales} icon="boy" />
                <DetailItem label="Female Children" value={member.numberOfFemales} icon="girl" />
                <DetailItem label="Father's Name" value={member.nameOfFather} icon="elderly" />
                <DetailItem
                  label="Father Life Status"
                  value={member.fatherLifeStatus}
                  icon="elderly"
                />
                <DetailItem label="Mother's Name" value={member.nameOfMother} icon="elderly" />
                <DetailItem
                  label="Mother's Life Status"
                  value={member.motherLifeStatus}
                  icon="elderly"
                />
                <DetailItem label="Next of Kin" value={member.nextOfKin} icon="contact_emergency" />
                <DetailItem
                  label="Next of Kin Relationship"
                  value={member.nextOfKinRelationship}
                  icon="contact_emergency"
                />
                <DetailItem
                  label="Next of Kin Phone Number"
                  value={member.nextOfKinPhone}
                  icon="contact_emergency"
                />
              </MDBox>
            </Grid>

            {/* Church Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(156, 39, 176, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(156, 39, 176, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="secondary">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>church</Icon>
                  Church Information
                </MDTypography>
                <DetailItem label="Department" value={member.department} icon="group" />
                <DetailItem label="Ministry" value={member.ministry} icon="volunteer_activism" />
                <DetailItem label="Status" value={member.status} icon="verified" />
                <DetailItem
                  label="Year of Conversion"
                  value={member.yearOfConversion}
                  icon="auto_awesome"
                />
                <DetailItem label="Baptized" value={member.areYouBaptized} icon="water_drop" />
                <DetailItem label="Baptism Type" value={member.baptismType} icon="waves" />
                <DetailItem label="Baptism Date" value={member.baptismDate} icon="event" />
                <DetailItem label="Baptism Church" value={member.baptismChurch} icon="church" />
                <DetailItem label="Baptized By" value={member.baptismBy} icon="person" />
              </MDBox>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(63, 81, 181, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(63, 81, 181, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="primary">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>info</Icon>
                  Additional Information
                </MDTypography>
                <DetailItem
                  label="Previous Church"
                  value={member.nameOfPreviousChurch}
                  icon="church"
                />
                <DetailItem
                  label="Place of Interest"
                  value={member.placeOfInterest}
                  icon="interests"
                />
                <DetailItem label="Will Pay Tithe" value={member.willYouPayTithe} icon="payment" />
                <DetailItem
                  label="Communicant"
                  value={member.communicant}
                  icon="record_voice_over"
                />
                <DetailItem
                  label="Will Pay Welfare"
                  value={member.willYouPayWelfare}
                  icon="volunteer_activism"
                />
                <DetailItem
                  label="Membership Date"
                  value={member.membershipDate}
                  icon="event_available"
                />
                <DetailItem label="Role" value={member.role} icon="admin_panel_settings" />
              </MDBox>
            </Grid>

            {/* Signature Section */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(0, 150, 136, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(0, 150, 136, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="info">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>edit</Icon>
                  Signature
                </MDTypography>
                {member.signature ? (
                  <MDBox
                    component="img"
                    src={`http://api.ofi-cia.com/laravel/storage/app/public/${member.signature}`}
                    alt="Signature"
                    sx={{
                      maxWidth: "300px",
                      maxHeight: "150px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                    }}
                  />
                ) : (
                  <MDTypography variant="body2" color="text">
                    No signature uploaded
                  </MDTypography>
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Footer Actions */}
        <MDBox
          display="flex"
          justifyContent="flex-end"
          mt={3}
          pt={2}
          sx={{ borderTop: "1px solid rgba(0,0,0,0.1)" }}
        >
          <MDButton variant="gradient" color="info" onClick={onClose}>
            <Icon sx={{ mr: 1 }}>close</Icon>
            Close
          </MDButton>
        </MDBox>
      </MDBox>
    </Dialog>
  );
};

MemberDetailsModal.propTypes = {
  member: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

// Main Hook
export default function useMemberData() {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .swal2-container {
        z-index: 9999 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { user, church } = useAuth();

  const [memberData, setMemberData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      {
        Header: "MemberID",
        accessor: "memberId",
        align: "center",
        width: "10%",
        Cell: MemberIdCell,
      },
      { Header: "First Name", accessor: "firstName", align: "left", width: "15%" },
      { Header: "Surname", accessor: "surname", align: "left", width: "15%" },
      { Header: "Gender", accessor: "gender", align: "left", width: "10%" },
      {
        Header: "Department",
        accessor: "department",
        align: "left",
        width: "15%",
        Cell: DepartmentCell,
      },
      {
        Header: "Ministry",
        accessor: "ministry",
        align: "left",
        width: "15%",
        Cell: MinistryCell,
      },
      { Header: "Status", accessor: "status", align: "center", width: "10%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "20%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleShow={handleShow}
          />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showModalOpen, setShowModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [ministries, setMinistries] = useState([]);

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`https://api.ofi-cia.com/api/departments/church/${church.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setDepartments(data.data || data.departments || data);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
    }
  };

  // Fetch ministries
  const fetchMinistries = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`https://api.ofi-cia.com/api/ministries/church/${church.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setMinistries(data.data || data.ministries || data);
    } catch (error) {
      console.error("Failed to fetch ministries:", error);
    }
  };

  // Fetch members
  const fetchMembers = async () => {
    try {
      setMemberData((prev) => ({ ...prev, isLoading: true }));
      const token = localStorage.getItem("authToken");
      const response = await fetch(`https://api.ofi-cia.com/api/members/church/${church.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const members = result.data || result.members || result;

      const rows = members.map((member, index) => ({
        id: member.id,
        serial: index + 1,
        memberId: member.memberID || "N/A",
        firstName: member.first_name || "N/A",
        surname: member.surname || "N/A",
        gender: member.gender || "N/A",
        department: member.department?.name || "No Department",
        departmentId: member.department?.id,
        ministry: member.ministry?.name || "",
        ministryId: member.ministry?.id,
        status: member.status ? "active" : "inactive",
        createdAt: member.created_at,
        middleName: member.middle_name || "",
        memberName: `${member.first_name} ${member.surname}`,
        dateOfBirth: member.date_of_birth || "",
        phoneNumber: member.phone || "",
        otherNumber: member.other_number || "",
        email: member.email || "",
        address: member.address || "",
        occupation: member.occupation || "",
        maritalStatus: member.marital_status || "",
        placeOfBirth: member.place_of_birth || "",
        nationality: member.nationality || "",
        homeTown: member.home_town || "",
        region: member.region || "",
        residentialAddress: member.residential_address || "",
        languagesSpoken: member.languages || "",
        whatsappNumber: member.whatsapp_number || "",
        postalAddress: member.postal_address || "",
        profession: member.profession || "",
        rank: member.rank || "",
        employment: member.employment || "",
        nameOfEmployer: member.name_of_employer || "",
        education: member.education || "",
        spouseName: member.spouse_name || "",
        spousePhoneNumber: member.spouse_phone || "",
        dateOfMarriage: member.date_of_marriage || "",
        byPastor: member.by_pastor || "",
        numberOfChildren: member.number_of_children || 0,
        numberOfMales: member.number_of_male || 0,
        numberOfFemales: member.number_of_female || 0,
        nameOfParents: member.name_of_parent || "",
        nameOfFather: member.name_of_father || "",
        fatherLifeStatus: member.father_life_status || "",
        nameOfMother: member.name_of_mother || "",
        motherLifeStatus: member.mother_life_status || "",
        nextOfKin: member.next_of_kin || "",
        nextOfKinPhone: member.next_of_kin_phone || "",
        nextOfKinRelationship: member.next_of_kin_relationship || "",
        yearOfConversion: member.year_of_conversion || "",
        areYouBaptized: member.are_you_baptized || "",
        baptismType: member.baptism_type || "",
        baptismDate: member.baptism_date || "",
        baptismChurch: member.baptism_church || "",
        baptismBy: member.baptism_by || "",
        baptismCertificate: member.baptism_certificate || "",
        nameOfPreviousChurch: member.name_of_previous_church || "",
        previousChurchAddress: member.previous_church_address || "",
        placeOfInterest: member.place_of_interest || "",
        willYouPayTithe: member.will_you_pay_tithe || "",
        communicant: member.communicant || "",
        willYouPayWelfare: member.will_you_pay_welfare || "",
        membershipDate: member.membership_date || "",
        status: member.status ? "active" : "inactive",
        signature: member.signature || "",
        role: member.role || "member",
        photo: member.photo || "",
        originalData: member,
      }));

      setMemberData((prev) => ({ ...prev, rows, isLoading: false, error: null }));
    } catch (error) {
      setMemberData((prev) => ({ ...prev, isLoading: false, error: error.message }));
      console.error("Fetch failed:", error);
    }
  };

  // Handle show member details
  const handleShow = (member) => {
    setSelectedMember(member);
    setShowModalOpen(true);
  };

  // Handle edit member
  const handleEdit = (member) => {
    setSelectedMember({
      ...member,
      memberName: `${member.firstName} ${member.surname}`,
      photoFile: null,
      signatureFile: null,
    });
    setEditModalOpen(true);
  };

  // Handle delete member
  const handleDelete = (member) => {
    setSelectedMember({
      ...member,
      memberName: `${member.firstName} ${member.surname}`,
    });
    setDeleteModalOpen(true);
  };

  // Handle edit/submit member
  const handleEditSubmit = async () => {
    try {
      // Validation
      if (!selectedMember?.firstName || !selectedMember?.surname) {
        Swal.fire({
          title: "Error!",
          text: "First name and surname are required",
          icon: "error",
          position: "top",
          toast: true,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      if (!selectedMember?.phoneNumber) {
        Swal.fire({
          title: "Error!",
          text: "Phone number is required",
          icon: "error",
          position: "top",
          toast: true,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      const token = localStorage.getItem("authToken");
      const url = selectedMember.id
        ? `https://api.ofi-cia.com/api/members/${selectedMember.id}`
        : "https://api.ofi-cia.com/api/members";

      // Create FormData
      const formData = new FormData();

      // Append all fields with proper API field names
      const fields = {
        // Personal Information
        first_name: selectedMember.firstName,
        middle_name: selectedMember.middleName,
        surname: selectedMember.surname,
        gender: selectedMember.gender,
        date_of_birth: selectedMember.dateOfBirth,
        marital_status: selectedMember.maritalStatus,

        // Contact Information
        phone: selectedMember.phoneNumber,
        other_number: selectedMember.otherNumber,
        whatsapp_number: selectedMember.whatsappNumber,
        email: selectedMember.email,

        // Address Information
        residential_address: selectedMember.residentialAddress,
        postal_address: selectedMember.postalAddress,
        region: selectedMember.region,
        home_town: selectedMember.homeTown,
        nationality: selectedMember.nationality,
        place_of_birth: selectedMember.placeOfBirth,

        // Professional Information
        profession: selectedMember.profession,
        rank: selectedMember.rank,
        employment: selectedMember.employment,
        name_of_employer: selectedMember.nameOfEmployer,
        education: selectedMember.education,

        // Family Information
        spouse_name: selectedMember.spouseName,
        spouse_phone: selectedMember.spousePhoneNumber,
        date_of_marriage: selectedMember.dateOfMarriage,
        by_pastor: selectedMember.byPastor,
        number_of_children: selectedMember.numberOfChildren || 0,
        number_of_male: selectedMember.numberOfMales || 0,
        number_of_female: selectedMember.numberOfFemales || 0,
        name_of_parent: selectedMember.nameOfParents,
        name_of_father: selectedMember.nameOfFather,
        father_life_status: selectedMember.fatherLifeStatus,
        name_of_mother: selectedMember.nameOfMother,
        mother_life_status: selectedMember.motherLifeStatus,
        next_of_kin: selectedMember.nextOfKin,
        next_of_kin_phone: selectedMember.nextOfKinPhone,
        next_of_kin_relationship: selectedMember.nextOfKinRelationship,

        // Church Information
        year_of_conversion: selectedMember.yearOfConversion,
        are_you_baptized: selectedMember.areYouBaptized,
        baptism_type: selectedMember.baptismType,
        baptism_date: selectedMember.baptismDate,
        baptism_church: selectedMember.baptismChurch,
        baptism_by: selectedMember.baptismBy,
        name_of_previous_church: selectedMember.nameOfPreviousChurch,
        previous_church_address: selectedMember.previousChurchAddress,
        place_of_interest: selectedMember.placeOfInterest,
        will_you_pay_tithe: selectedMember.willYouPayTithe,
        communicant: selectedMember.communicant,
        will_you_pay_welfare: selectedMember.willYouPayWelfare,
        membership_date: selectedMember.membershipDate,
        role: selectedMember.role || "member",
        church_id: church.id,
        status: selectedMember.status === "active" ? 1 : 0,
      };

      // Append all fields
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value || "");
      });

      // Append relationships
      if (selectedMember.departmentId) {
        formData.append("department_id", selectedMember.departmentId);
      }
      if (selectedMember.ministryId) {
        formData.append("ministry_id", selectedMember.ministryId);
      }

      // Append files
      if (selectedMember.photoFile) {
        formData.append("photo", selectedMember.photoFile);
      }
      if (selectedMember.signatureFile) {
        formData.append("signature", selectedMember.signatureFile);
      }

      // Add method for updates
      if (selectedMember.id) {
        formData.append("_method", "PUT");
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      console.log("Save response:", data);

      if (!response.ok) {
        throw new Error(data.message || "Failed to save member");
      }

      Swal.fire({
        title: "Success!",
        text: selectedMember?.id ? "Member updated successfully!" : "Member created successfully!",
        icon: "success",
        timer: 3000,
        timerProgressBar: true,
      });

      fetchMembers();
      setEditModalOpen(false);
    } catch (error) {
      Swal.fire("Error!", error.message, "error");
      console.error("Save error:", error);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`https://api.ofi-cia.com/api/members/${selectedMember.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(await response.text());

      Swal.fire("Deleted!", "Member has been deleted.", "success");
      fetchMembers();
    } catch (error) {
      Swal.fire("Error!", error.message, "error");
    } finally {
      setDeleteModalOpen(false);
    }
  };

  // Handle add new member
  const handleAddMember = () => {
    setSelectedMember({
      id: null,
      memberName: "",
      firstName: "",
      middleName: "",
      surname: "",
      gender: "",
      department: "",
      departmentId: null,
      ministry: "",
      ministryId: null,
      status: "active",
      photoFile: null,
      signatureFile: null,
      photo: "",
      signature: "",
      dateOfBirth: "",
      phoneNumber: "",
      otherNumber: "",
      whatsappNumber: "",
      email: "",
      residentialAddress: "",
      postalAddress: "",
      region: "",
      homeTown: "",
      nationality: "",
      placeOfBirth: "",
      maritalStatus: "",
      profession: "",
      rank: "",
      employment: "",
      nameOfEmployer: "",
      education: "",
      spouseName: "",
      spousePhoneNumber: "",
      dateOfMarriage: "",
      byPastor: "",
      numberOfChildren: 0,
      numberOfMales: 0,
      numberOfFemales: 0,
      nameOfParents: "",
      nameOfFather: "", // NEW: Initialize name_of_father
      fatherLifeStatus: "", // NEW: Initialize father_life_status
      nameOfMother: "", // NEW: Initialize name_of_mother
      motherLifeStatus: "", // NEW: Initialize mother_life_status
      nextOfKin: "",
      nextOfKinPhone: "",
      nextOfKinRelationship: "",
      yearOfConversion: "",
      areYouBaptized: "",
      baptismType: "",
      baptismDate: "",
      baptismChurch: "",
      baptismBy: "",
      nameOfPreviousChurch: "",
      previousChurchAddress: "",
      placeOfInterest: "",
      willYouPayTithe: "",
      communicant: "",
      willYouPayWelfare: "",
      membershipDate: "",
      role: "member",
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => window.print();

  useEffect(() => {
    if (church?.id) {
      fetchDepartments();
      fetchMinistries();
      fetchMembers();
    }
  }, [user, church]);

  return {
    ...memberData,
    handleShow,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    showModalOpen,
    setShowModalOpen,
    selectedMember,
    setSelectedMember,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMember,
    departments,
    ministries,
    MemberDetailsModal,
  };
}
